import { LoadingSpinner } from "components/LoadingSpinner";
import { ModalAddressBooking } from "components/ModalAddressBooking";
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type React from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaWallet } from "react-icons/fa";
import type { JSX } from "react/jsx-runtime";
import { fetchBookingConditions } from "services/fetch-booking-condition";
import type { Root as RootProfile } from "services/get-profile";
import { Formatter } from "utils/formatters";
import { getCookie } from "utils/functions-cookie";
import type { BookingCondition } from "../listing-stay-detail/components/type";
import type { Room } from "../listing-stay-detail/type";

type MobileFooterStickyProps = {
  room: Room | null;
  hotelId: number;
  token: string;
  profile: RootProfile;
  checkin: string;
  numNights: number;
  setConditionsDataGlobal: (data: BookingCondition | null) => void;
  adults: number;
  childrenAgeQuantity: number;
};

const MobileFooterSticky = ({
  room,
  hotelId,
  token,
  profile,
  setConditionsDataGlobal,
  checkin,
  numNights,
  adults,
  childrenAgeQuantity,
}: MobileFooterStickyProps) => {
  const authToken = getCookie("auth_token");
  const { setLoading, isBooking, setIsBooking, loading } = useHotels();

  const [modalAddressBooking, setModalAddressBooking] = useState(false);
  const convertEndDate = (date: string, nights: number) => {
    const checkinWithTimezone = date + "T00:00:00-03:00";
    const newDate = new Date(checkinWithTimezone);
    const dateFormattedPlusNight = format(
      newDate.setDate(newDate.getDate() + nights),
      "dd 'de' MMM",
      { locale: ptBR }
    );
    return dateFormattedPlusNight;
  };
  const checkinWithTimezone = checkin + "T00:00:00-03:00";
  const convertedStartDate = new Date(checkinWithTimezone);
  const formattedStartDate = format(convertedStartDate, "dd 'de' MMM", {
    locale: ptBR,
  });

  const handleClickCondition = async () => {
    setLoading(true);
    try {
      if (profile?.address === null) {
        setLoading(false);
        setModalAddressBooking(true);
        return;
      }

      if (!authToken || !room) {
        toast.error("Erro ao tentar buscar as condições de reserva");
        return 0;
      }

      const data = await fetchBookingConditions({
        hotelId: hotelId,
        roomId: [room.id],
        token: token,
        authToken: authToken,
        checkInDate: checkin,
      });

      if (!data) {
        toast.error(
          "Tivemos um problema ao buscar as condições de reserva. Tente novamente mais tarde."
        );
        return 0;
      }

      setIsBooking(true);
      setConditionsDataGlobal(data);
    } catch (error) {
      setLoading(false);
      setIsBooking(false);
      toast.error(
        "Tivemos um problema ao buscar as condições de reserva. Tente novamente mais tarde."
      );
    } finally {
      setLoading(false);

      setTimeout(() => {
        if (document.getElementById("reserve-container")) {
          document.getElementById("reserve-container")?.scrollIntoView({
            behavior: "smooth",
          });
        }
      }, 100);
    }

    if (isBooking) {
      setIsBooking(false);
      if (document.getElementById("topo")) {
        document.getElementById("topo")?.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  };

  if (!room) {
    return null;
  }

  let buttonText:
    | string
    | number
    | boolean
    | JSX.Element
    | Iterable<React.ReactNode>
    | null
    | undefined;
  if (isBooking) {
    buttonText = "Cancelar";
  } else if (loading) {
    buttonText = <LoadingSpinner />;
  } else {
    buttonText = "Reservar";
  }

  if (!profile) {
    return null;
  }

  return (
    <>
      {modalAddressBooking && (
        <ModalAddressBooking setModalAddressBooking={setModalAddressBooking} />
      )}
      <div className="block lg:hidden fixed bottom-0 inset-x-0 py-2 sm:py-3 bg-white dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-6000 z-40">
        <div className="px-2 gap-2 flex items-center justify-between">
          <div className="flex flex-col items-start w-1/3">
            <span className="block text-xs underline underline-offset-2 font-normal">
              {formattedStartDate} - {convertEndDate(checkin, numNights)}
            </span>
            <span className="block text-base underline underline-offset-2 font-normal">
              {adults + childrenAgeQuantity}
              <span className="ml-1 text-sm font-normal">
                {adults + childrenAgeQuantity > 1 ? "Hóspedes" : "Hóspede"}
              </span>
            </span>
          </div>
          <div className="h-8 w-[1px] bg-neutral-400 dark:bg-neutral-700"></div>
          {profile.wallet.balance > 0 || profile?.subscription?.active ? (
            <div className="flex items-end w-1/2">
              <div className="flex items-center">
              <FaWallet className="mr-1" size={18} />
                <span className="ml-1 text-base font-semibold dark:text-neutral-200">
                  {Formatter.prettyPriceHotel(room.price, profile)}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <span className="font-semibold text-sm">Você não tem</span>
              <span className="font-semibold text-sm">ViroCash disponível</span>
            </div>
          )}
          <div className="flex items-center">
            {profile.wallet.balance > 0 || profile?.subscription?.active ? (
              <button
                className={`${
                  isBooking
                    ? "bg-[--yellow] hover:bg-[--yellow-dark] text-neutral-200 py-2 px-2 rounded-full text-[14px]"
                    : "bg-[--blue] hover:bg-[--blue-dark] text-neutral-200 py-2 px-2 rounded-full text-[14px]"
                }   transition-colors duration-300 ease-in-out
                    disabled:bg-[#b2d0e0] disabled:cursor-not-allowed
                  `}
                onClick={handleClickCondition}
                disabled={loading || profile?.wallet.balance === 0}
              >
                {buttonText}
              </button>
            ) : (
              <a
                href={"/plans"}
                className="bg-[--blue] hover:bg-[--blue-dark] text-neutral-200 py-2 px-2 rounded-full text-[14px]"
              >
                Faça sua assinatura
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileFooterSticky;
